import { useBusStore, useGameBusStore } from '@/client/store/bus.ts'
import { GameType, Invalid, VALID, Validity } from '@/shared/types/state.ts'
import {
    AnyGameMutation,
    createMutation,
    GameMutation,
    GameMutationClassType,
    GameMutationParams,
    gameMutations,
    PingCard,
} from '@/shared/state/gameMutations.ts'
import { useHistoryStore } from '@/client/store/history.ts'
import { usePlayersStore } from '@/client/state/players.ts'
import { Player } from '@/shared/model/Player.ts'
import { MutationHistoryEntry } from '@/shared/types/history.ts'
import { deserializeGameMutation } from '@/shared/serialization.ts'
import { AnyCardRegion } from '@/shared/types/model.ts'
import { getLogger } from '@/shared/registries.ts'
import { useGameStateStore } from '@/client/store/gameState.ts'
import { useEngineStore } from '@/client/store/engine.ts'
import { engineGesture, isEngineProjecting } from '@/client/engine/driver.ts'

/**
 * Apply the mutation locally, if it's valid.
 */
export function applyMutationIfValid(gameMutation: AnyGameMutation) {
    const validity = gameMutation.canApply()
    if (validity.isValid) {
        gameMutation.apply()

        try {
            // The rules engine's table moves are narrated by the engine ( see engine/driver.ts )
            useHistoryStore().addGameMutation(gameMutation, undefined, !isEngineProjecting())

            if (gameMutation instanceof PingCard) {
                // This one is kinda special : we update the game bus instead of the game state
                useGameBusStore().pingCard(gameMutation.params.card.oid)
            }
        } catch (error) {
            getLogger().captureException(error)
        }
    }
}

// The mutations the user keeps in a game run by the rules engine : arranging
// the cards of a region, and the UI ones ( arrows, scale, separators, ping )
const ENGINE_USER_MUTATIONS = /^(moveCard|UI_.*)$/

/**
 * If the mutation is valid, apply it.
 * Alert the user if the mutation is invalid.
 */
export function dispatchMutation(gameMutation: AnyGameMutation) {
    const gameState = useGameStateStore()
    const bus = useBusStore()

    // With the rules engine, the table changes only through the engine : the
    // user's moves are read as decisions ( see engine/gestures.ts ), the
    // cosmetic ones kept as they are.
    if (
        gameState.gameType == GameType.Engine &&
        !isEngineProjecting() &&
        !ENGINE_USER_MUTATIONS.test(gameMutation.name)
    ) {
        // Told beside the Pass, not over the table
        const validity = engineGesture(gameMutation)
        if (!validity.isValid) {
            useEngineStore().tellNotice(validity.reason)
        }
        return validity
    }

    const validity = gameMutation.canApply()
    if (!validity.isValid) {
        bus.alertWarning(validity.reason)
        return validity
    }

    applyMutationIfValid(gameMutation)

    return VALID
}

/**
 * A player act on the game : create the mutation and dispatch it.
 */
export function act<
    ParamsType extends GameMutationParams,
    GMClass extends GameMutation<ParamsType>,
>(
    gameMutationClass: GameMutationClassType<ParamsType, GMClass>,
    author: Player,
    params: ParamsType,
): Validity {
    const gameMutation = createMutation(gameMutationClass, author, params)
    return dispatchMutation(gameMutation)
}

/**
 * Shorthand when self player act on the game.
 */
export function actSelf<
    ParamsType extends GameMutationParams,
    GMClass extends GameMutation<ParamsType>,
>(gameMutationClass: GameMutationClassType<ParamsType, GMClass>, params: ParamsType): Validity {
    const players = usePlayersStore()
    // spectators can't act on the game
    if (players.isSpectator) {
        return Invalid("Spectators can't act on the game")
    }
    if (!players.selfPlayer) {
        throw new Error('Cannot act without a self player defined')
    }
    return act(gameMutationClass, players.selfPlayer, params)
}

/**
 * Cancel
 */

export function cancelMutation(mutationEntry: MutationHistoryEntry) {
    if (!mutationEntry.isUserCancellable) {
        throw new Error('Cannot cancel this type of mutation')
    }
    const mutation = deserializeGameMutation(mutationEntry.serializedMutation)
    dispatchMutation(mutation.getCancelMutation())
}

/**
 * Shuffle Card Region
 */

export function shuffleCardRegion(cardRegion: AnyCardRegion) {
    const players = usePlayersStore()
    const history = useHistoryStore()
    const bus = useBusStore()

    if (!players.selfPlayer) {
        return
    }

    // Prevent multiple shuffle
    const lastMutation = history.getLastMutationForPlayer(players.selfPlayer.oid)
    if (lastMutation && lastMutation.serializedMutation.name == 'shuffle') {
        bus.alertWarning(`This stack is already shuffled.`)
        return
    }

    gameMutations.shuffle.actSelf({
        cardRegion,
        cardsOrder: cardRegion.generateShuffledCardsOrder(),
        previousCardsOrder: [...cardRegion.cardsOid],
    })
}

/**
 * Roll Random Result ( Coin Flip / D6 Roll )
 */

export function rollRandomResult(randomType: 'coin' | 'd6') {
    const max = randomType === 'coin' ? 2 : 6
    const result = Math.floor(Math.random() * max) + 1
    gameMutations.randomResult.actSelf({ randomType, result })
}
